import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, ExternalLink, Plus, Trash2, X, Tag, Wallet, Film, UserX, UserCheck, Archive, ArchiveRestore, Link2, StickyNote, ShieldCheck, Gauge, Crown } from "lucide-react";
import { API, authHeaders, ROLE_BADGE_STYLES, QUALITY_STATUSES, JOB_STATUS_STYLES, JOB_STATUS_LABELS } from "@/constants/roles";
import { usePerms } from "@/components/PermsContext";
import { fmtMoney, fmtNum } from "@/utils/currency";
import { WorkerPayoutSection } from "@/components/admin/WorkerPayoutSection";

const TAG_SUGGESTIONS = ["Strong Acting", "Great Camera Work", "Great Environments", "Fast Turnaround", "Strong Comedy", "Cinematic", "Reliable", "Needs Direction"];
const LINK_KINDS = ["link", "youtube", "drive", "website", "upload"];

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";
const labelCls = "mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]";

const Section = ({ title, children, testid }) => (
  <section data-testid={testid} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
    <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">{title}</h3>
    <div className="mt-4">{children}</div>
  </section>
);

export const AdminWorkerProfile = ({ workerId, onBack, onChanged, onOpenVideo }) => {
  const { has, role } = usePerms();
  const [p, setP] = useState(null);
  const [notes, setNotes] = useState("");
  const [payRate, setPayRate] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [portfolio, setPortfolio] = useState([]);
  const [capLimit, setCapLimit] = useState("");
  const [newLink, setNewLink] = useState({ url: "", kind: "link", note: "" });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [topChoices, setTopChoices] = useState(null); // 409 replacement picker: current Top Animators

  const load = useCallback(() => {
    axios
      .get(`${API}/admin/team/${workerId}`, { headers: authHeaders() })
      .then((r) => {
        const d = r.data;
        setP(d);
        setNotes(d.private.admin_notes || "");
        setPayRate(d.financial.pay_rate || "");
        setPayNotes(d.payment_notes || "");
        setTags(d.private.tags || []);
        setPortfolio(d.portfolio || []);
        setCapLimit(d.identity.max_concurrent ?? "");
      })
      .catch(() => toast.error("Could not load worker profile."));
  }, [workerId]);

  useEffect(load, [load]);

  const patch = async (payload, successMsg) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/team/${workerId}`, payload, { headers: authHeaders() });
      if (successMsg) toast.success(successMsg);
      load();
      onChanged?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (deactivated, archived) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/users/${workerId}/status`, { deactivated, archived }, { headers: authHeaders() });
      toast.success(deactivated ? "Production access disabled." : archived ? "Worker archived — history preserved." : "Worker updated.");
      load();
      onChanged?.();
    } catch { toast.error("Could not update worker."); } finally { setBusy(false); }
  };

  const deleteWorker = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/admin/users/${workerId}`, { headers: authHeaders() });
      toast.success(`Worker permanently deleted. ${data.unclaimed_jobs} job(s) returned to available, ${data.removed_payments} payment record(s) removed.`);
      onChanged?.();
      onBack();
    } catch { toast.error("Could not delete worker."); } finally { setBusy(false); }
  };

  const addTag = (t) => {
    const tag = t.trim();
    if (tag && !tags.includes(tag)) patch({ tags: [...tags, tag] }, null);
    setNewTag("");
  };

  const addPortfolioLink = () => {
    if (!newLink.url.trim()) return;
    patch({ portfolio: [...portfolio, { ...newLink, url: newLink.url.trim() }] }, "Portfolio link added.");
    setNewLink({ url: "", kind: "link", note: "" });
  };

  const toggleManager = async () => {
    setBusy(true);
    try {
      const makeManager = identity.account_role !== "manager";
      await axios.post(`${API}/admin/users/${workerId}/manager`, { manager: makeManager }, { headers: authHeaders() });
      toast.success(makeManager ? "Promoted to Production Manager." : "Demoted back to worker.");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not change role.");
    } finally {
      setBusy(false);
    }
  };

  const setEligibility = async (eligibility) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/${workerId}/animator-eligibility`, { eligibility }, { headers: authHeaders() });
      toast.success("Animator eligibility updated — applies to future job claims only.");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not update eligibility.");
    } finally {
      setBusy(false);
    }
  };

  const markTopAnimator = async (replaceId = null) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/${workerId}/top-animator`, replaceId ? { replace_user_id: replaceId } : {}, { headers: authHeaders() });
      toast.success(replaceId ? "Top Animator replaced." : "Marked as Top Animator.");
      setTopChoices(null);
      load();
      onChanged?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === "top_animator_limit") {
        setTopChoices(d.current || []); // owner must intentionally pick who gets replaced
      } else {
        toast.error(typeof d === "string" ? d : "Could not mark Top Animator.");
      }
    } finally {
      setBusy(false);
    }
  };

  const unmarkTopAnimator = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/users/${workerId}/top-animator`, { headers: authHeaders() });
      toast.success("Top Animator marker removed.");
      load();
      onChanged?.();
    } catch { toast.error("Could not remove the marker."); } finally { setBusy(false); }
  };

  if (!p) {
    return <p data-testid="profile-loading" className="py-20 text-center text-sm font-medium text-[var(--pv-text-4)]"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>;
  }

  const { identity, production, financial } = p;
  const qs = QUALITY_STATUSES[p.private.quality_status] || QUALITY_STATUSES.good_standing;

  return (
    <div data-testid="worker-profile" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button data-testid="profile-back-btn" onClick={onBack} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2 text-xs font-semibold text-[var(--pv-text-2)] hover:text-[var(--pv-text)]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to team
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {has("deactivate_worker") && (identity.deactivated ? (
            <button data-testid="profile-reactivate-btn" onClick={() => setStatus(false, identity.archived)} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-4 py-2 text-xs font-bold text-emerald-300 hover:bg-emerald-500/25">
              <UserCheck className="h-3.5 w-3.5" /> Reactivate
            </button>
          ) : (
            <button data-testid="profile-deactivate-btn" onClick={() => setStatus(true, identity.archived)} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 px-4 py-2 text-xs font-bold text-amber-300 hover:bg-amber-500/25">
              <UserX className="h-3.5 w-3.5" /> Deactivate
            </button>
          ))}
          {has("deactivate_worker") && (
            <button
              data-testid="profile-archive-btn"
              onClick={() => setStatus(identity.deactivated, !identity.archived)}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:text-[var(--pv-text)]"
            >
              {identity.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
              {identity.archived ? "Unarchive" : "Archive"}
            </button>
          )}
          {!has("delete_worker") ? null : confirmDelete ? (
            <span className="flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1.5">
              <span className="text-[11px] font-bold text-rose-300">Delete permanently? Removes account, payments, submissions; open jobs return to available.</span>
              <button data-testid="profile-delete-confirm-btn" onClick={deleteWorker} disabled={busy} className="rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-rose-600">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes, delete"}
              </button>
              <button onClick={() => setConfirmDelete(false)} className="text-[11px] font-bold text-[var(--pv-text-3)]">Cancel</button>
            </span>
          ) : (
            <button data-testid="profile-delete-btn" onClick={() => setConfirmDelete(true)} className="flex items-center gap-1.5 rounded-full bg-rose-500/10 border border-rose-500/30 px-4 py-2 text-xs font-bold text-rose-400 hover:bg-rose-500/20">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-4">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-cyan-500/15 font-display text-2xl font-bold text-cyan-300">
            {(identity.name || identity.email).slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 data-testid="profile-name" className="font-display text-2xl font-bold text-[var(--pv-text)]">{identity.name || identity.email}</h2>
              {identity.is_top_animator && (
                <span data-testid="profile-top-animator-badge" className="flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-0.5 text-[11px] font-bold text-amber-300">
                  <Crown className="h-3 w-3" /> Top Animator
                </span>
              )}
              {identity.account_role === "manager" && <span className="rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-0.5 text-[11px] font-bold text-violet-300">Production Manager</span>}
              {identity.account_role === "manager"
                ? identity.worker_role && <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${ROLE_BADGE_STYLES[identity.worker_role] || ""}`}>{identity.worker_role}</span>
                : (identity.display_role || identity.worker_role) && <span data-testid="profile-role-badge" className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${ROLE_BADGE_STYLES[identity.display_role] || ROLE_BADGE_STYLES[identity.worker_role] || ""}`}>{identity.display_role || identity.worker_role}</span>}
              <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${qs.style}`}>{qs.label}</span>
              {identity.deactivated && <span className="rounded-full border border-rose-500/30 bg-rose-500/15 px-2.5 py-0.5 text-[11px] font-bold uppercase text-rose-300">Deactivated</span>}
              {identity.archived && <span className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-raised)] px-2.5 py-0.5 text-[11px] font-bold uppercase text-[var(--pv-text-3)]">Archived</span>}
            </div>
            <p className="mt-1 text-sm text-[var(--pv-text-4)]">
              {identity.email}
              {identity.hire_date && <> · crew since {new Date(identity.hire_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</>}
              {identity.invited && <> · invited directly</>}
            </p>
            {tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span key={t} data-testid={`profile-tag-${t.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-0.5 text-[10px] font-bold text-cyan-300">
                    <Tag className="h-2.5 w-2.5" /> {t}
                    <button onClick={() => patch({ tags: tags.filter((x) => x !== t) })} className="text-cyan-300/60 hover:text-rose-400" aria-label={`Remove tag ${t}`}><X className="h-2.5 w-2.5" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {[
            { label: "Active", value: production.active_jobs },
            { label: "Awaiting review", value: production.awaiting_review },
            { label: "Completed", value: production.completed_jobs },
            { label: "Revisions", value: production.total_revisions },
            { label: "Avg turnaround", value: production.avg_completion_hours != null ? `${production.avg_completion_hours}h` : "—" },
            ...(financial.total_earned != null ? [{ label: "Earned", value: fmtMoney(financial.total_earned, 0) }] : []),
            ...(financial.total_paid != null ? [{ label: "Paid", value: fmtMoney(financial.total_paid, 0) }] : []),
            ...(financial.amount_owed != null ? [{ label: "Owed", value: fmtMoney(financial.amount_owed, 0) }] : []),
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-[var(--pv-subtle)] px-3 py-3 text-center">
              <p className="font-display text-lg font-bold text-[var(--pv-text)]">{s.value}</p>
              <p className="text-[9px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{s.label}</p>
            </div>
          ))}
        </div>

        {role === "admin" && identity.account_role === "worker" && (
          <div data-testid="profile-capacity-row" className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-3">
            <span className="flex items-center gap-1.5 text-xs font-bold text-[var(--pv-text-3)]"><Gauge className="h-4 w-4 text-cyan-400" /> Active job limit</span>
            <input
              data-testid="profile-capacity-input"
              type="number"
              min="1"
              max="50"
              value={capLimit}
              onChange={(e) => setCapLimit(e.target.value)}
              placeholder="Default"
              className="w-28 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none"
            />
            <button
              data-testid="profile-capacity-save"
              onClick={() => patch({ max_concurrent: capLimit === "" ? 0 : Math.max(1, Math.min(50, parseInt(capLimit, 10) || 1)) }, capLimit === "" ? "Capacity reset to the studio default." : "Capacity limit saved.")}
              disabled={saving}
              className="flex items-center gap-1 rounded-full bg-cyan-500 px-3.5 py-1.5 text-[11px] font-bold text-white hover:bg-cyan-600 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
            </button>
            <span className="text-[10px] font-semibold text-[var(--pv-text-4)]">Blank = studio default (Settings → Worker capacity)</span>
          </div>
        )}
      </div>

      {(role === "admin" || has("manage_roles")) && (
        <Section title="Role & access" testid="profile-role-access">
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <p className={labelCls}>Animator tier eligibility</p>
              <div className="flex flex-wrap gap-2" data-testid="eligibility-options">
                {[
                  { id: "standard", label: "Standard only" },
                  { id: "hq", label: "HQ only" },
                  { id: "both", label: "Standard + HQ" },
                  { id: "none", label: "Not an animator" },
                ].map((opt) => {
                  const active = (identity.animator_eligibility || "") === opt.id;
                  return (
                    <button
                      key={opt.id}
                      data-testid={`eligibility-${opt.id}-btn`}
                      onClick={() => setEligibility(opt.id)}
                      disabled={busy || !has("manage_roles") || active}
                      className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors disabled:opacity-70 ${
                        active
                          ? "border-cyan-500/40 bg-cyan-500/15 text-cyan-300"
                          : "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-3)] hover:border-cyan-500/30 hover:text-cyan-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[11px] text-[var(--pv-text-4)]" data-testid="eligibility-current">
                Effective roles: <span className="font-bold text-[var(--pv-text-2)]">{identity.animator_roles?.length ? identity.animator_roles.join(" + ") : "none"}</span>
                {!identity.animator_eligibility && " (inherited from worker role)"}
              </p>
            </div>
            <div>
              <p className={labelCls}>Production Manager access</p>
              {role === "admin" ? (
                <button data-testid="profile-manager-toggle-btn" onClick={toggleManager} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-4 py-2 text-xs font-bold text-violet-300 transition-colors hover:bg-violet-500/20 disabled:opacity-60">
                  <ShieldCheck className="h-3.5 w-3.5" /> {identity.account_role === "manager" ? "Demote to worker" : "Make Production Manager"}
                </button>
              ) : (
                <p className="text-xs text-[var(--pv-text-4)]">{identity.account_role === "manager" ? "This person is a Production Manager." : "Only the owner can grant manager access."}</p>
              )}
              <p className="mt-2 text-[11px] text-[var(--pv-text-4)]">Managers keep their animator eligibility — they get the manager console plus a "My Work" section to keep claiming jobs.</p>
            </div>
            {role === "admin" && (identity.animator_roles || []).length > 0 && (
              <div data-testid="top-animator-controls">
                <p className={labelCls}>Top Animator <span className="font-normal text-[var(--pv-text-4)]">— your manual quality marker (max 3)</span></p>
                {identity.is_top_animator ? (
                  <button data-testid="top-animator-remove-btn" onClick={unmarkTopAnimator} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-xs font-bold text-amber-300 transition-colors hover:bg-amber-400/20 disabled:opacity-60">
                    <Crown className="h-3.5 w-3.5" /> Remove Top Animator
                  </button>
                ) : (
                  <button data-testid="top-animator-mark-btn" onClick={() => markTopAnimator()} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] transition-colors hover:border-amber-400/40 hover:text-amber-300 disabled:opacity-60">
                    <Crown className="h-3.5 w-3.5" /> Mark as Top Animator
                  </button>
                )}
                {topChoices && (
                  <div data-testid="top-animator-replace-picker" className="mt-3 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4">
                    <p className="text-xs font-bold text-amber-300">You already have 3 Top Animators — who should be replaced?</p>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {topChoices.map((t) => (
                        <button key={t.id} data-testid={`top-animator-replace-${t.id}`} onClick={() => markTopAnimator(t.id)} disabled={busy}
                          className="rounded-full border border-amber-400/40 bg-[var(--pv-card)] px-3.5 py-1.5 text-xs font-bold text-amber-200 transition-colors hover:bg-amber-400/15 disabled:opacity-60">
                          Replace {t.name}
                        </button>
                      ))}
                      <button data-testid="top-animator-replace-cancel" onClick={() => setTopChoices(null)} className="rounded-full px-3.5 py-1.5 text-xs font-bold text-[var(--pv-text-4)] hover:text-[var(--pv-text)]">Cancel</button>
                    </div>
                  </div>
                )}
                <p className="mt-2 text-[11px] text-[var(--pv-text-4)]">Purely a badge — never affects eligibility, pay, permissions, priority or analytics.</p>
              </div>
            )}
          </div>
          <p className="mt-4 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2.5 text-[11px] font-semibold text-[var(--pv-text-3)]">
            Role changes apply to future job claims only — existing jobs, locked-in rates and payment history are never changed.
          </p>
        </Section>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Portfolio" testid="profile-portfolio">
          {portfolio.length === 0 ? (
            <p className="text-sm text-[var(--pv-text-4)]">No portfolio material yet — add links to remember their style and level.</p>
          ) : (
            <ul className="space-y-2">
              {portfolio.map((item) => (
                <li key={item.id} className="flex items-center gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2.5">
                  <Link2 className="h-4 w-4 shrink-0 text-cyan-400" />
                  <div className="min-w-0 flex-1">
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="block truncate text-xs font-semibold text-cyan-400 hover:text-cyan-300">{item.url}</a>
                    {item.note && <p className="text-[11px] text-[var(--pv-text-4)]">{item.note}</p>}
                  </div>
                  <span className="shrink-0 rounded-full bg-[var(--pv-raised)] px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--pv-text-3)]">{item.kind}</span>
                  <button onClick={() => patch({ portfolio: portfolio.filter((x) => x.id !== item.id) })} className="shrink-0 text-rose-400 hover:text-rose-300" aria-label="Remove link"><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <input data-testid="portfolio-url-input" value={newLink.url} onChange={(e) => setNewLink((l) => ({ ...l, url: e.target.value }))} placeholder="https://youtube.com/... or Drive link" className={`${inputCls} min-w-0 flex-1`} />
            <select data-testid="portfolio-kind-select" value={newLink.kind} onChange={(e) => setNewLink((l) => ({ ...l, kind: e.target.value }))} className={`${inputCls} w-32`}>
              {LINK_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input data-testid="portfolio-note-input" value={newLink.note} onChange={(e) => setNewLink((l) => ({ ...l, note: e.target.value }))} placeholder="Note (style, level...)" className={`${inputCls} min-w-0 flex-1`} />
            <button data-testid="portfolio-add-btn" onClick={addPortfolioLink} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-cyan-600">
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
        </Section>

        {(role === "admin" || has("view_payment_details")) && (
        <Section title="Payment methods — worker-managed" testid="profile-payment">
          <WorkerPayoutSection workerId={workerId} onChanged={onChanged} />
        </Section>
        )}
        {role === "admin" && (
        <Section title="Pay rate & private notes" testid="profile-pay-settings">
          <div className="mt-1">
            <label className={labelCls}>Admin payment notes (private)</label>
            <input data-testid="pm-notes-input" value={payNotes} onChange={(e) => setPayNotes(e.target.value)} placeholder="e.g. prefers monthly payout" className={inputCls} />
          </div>
          <div className="mt-3">
            <label className={labelCls}>Default / base rate</label>
            <input data-testid="pay-rate-input" value={payRate} onChange={(e) => setPayRate(e.target.value)} placeholder="e.g. $110 per video" className={inputCls} />
          </div>
          <button
            data-testid="payment-save-btn"
            onClick={() => patch({ payment_notes: payNotes, pay_rate: payRate }, "Payment info saved.")}
            disabled={saving}
            className="mt-4 flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save payment info
          </button>
        </Section>
        )}
      </div>

      <Section title="Work history — jobs & produced videos" testid="profile-work-history">
        {p.work_history.length === 0 ? (
          <p className="text-sm text-[var(--pv-text-4)]">No jobs claimed yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-[var(--pv-border)] text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
                  <th className="pb-2 pr-4">Job</th><th className="pb-2 pr-4">Status</th><th className="pb-2 pr-4">Pay</th><th className="pb-2">Completed</th>
                </tr>
              </thead>
              <tbody>
                {p.work_history.map((j) => (
                  <tr key={j.id} data-testid={`history-row-${j.id}`} className="border-b border-[var(--pv-border)] last:border-0">
                    <td className="py-2.5 pr-4 font-semibold text-[var(--pv-text)]">{j.title}</td>
                    <td className="py-2.5 pr-4"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${JOB_STATUS_STYLES[j.status] || ""}`}>{JOB_STATUS_LABELS[j.status] || j.status}</span></td>
                    <td className="py-2.5 pr-4 text-[var(--pv-text-2)]">{fmtMoney(j.estimated_pay ?? 0)}</td>
                    <td className="py-2.5 text-[var(--pv-text-4)]">{j.completed_at ? new Date(j.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {p.videos.length > 0 && (
          <div className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Production library</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {p.videos.map((v) => (
                <button
                  key={v.id}
                  data-testid={`profile-video-${v.id}`}
                  onClick={() => onOpenVideo?.(v.id)}
                  className="flex items-center gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-3 text-left transition-colors hover:border-cyan-500/30"
                >
                  <Film className="h-4 w-4 shrink-0 text-cyan-400" />
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold text-[var(--pv-text)]">{v.public_title || v.internal_title}</p>
                    <p className="text-[11px] text-[var(--pv-text-4)]">
                      {v.status} · {(v.performance?.views || 0).toLocaleString()} views
                      {v.youtube_url && <> · <span className="text-cyan-400">published</span></>}
                    </p>
                  </div>
                  <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 text-[var(--pv-text-4)]" />
                </button>
              ))}
            </div>
          </div>
        )}
      </Section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Private — notes & quality (never visible to worker)" testid="profile-private">
          <label className={labelCls}>Quality status</label>
          {role === "admin" ? (
            <select
              data-testid="quality-select"
              value={p.private.quality_status}
              onChange={(e) => patch({ quality_status: e.target.value }, "Quality status updated.")}
              className={inputCls}
            >
              {Object.entries(QUALITY_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          ) : (
            <p data-testid="quality-readonly" className="rounded-2xl bg-[var(--pv-subtle)] px-4 py-2.5 text-sm font-semibold text-[var(--pv-text-2)]">{qs.label}</p>
          )}
          {p.private.quality_history.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {p.private.quality_history.slice().reverse().map((h, i) => (
                <li key={i} className="rounded-xl bg-[var(--pv-subtle)] px-3 py-2 text-[11px] text-[var(--pv-text-3)]">
                  {(QUALITY_STATUSES[h.from] || {}).label || h.from} → <span className="font-bold">{(QUALITY_STATUSES[h.to] || {}).label || h.to}</span> · {new Date(h.at).toLocaleDateString()}
                </li>
              ))}
            </ul>
          )}
          <label className={`${labelCls} mt-4`}>Private notes</label>
          <textarea data-testid="private-notes-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} placeholder="Only you can see this..." disabled={!has("add_internal_notes")} />
          {has("add_internal_notes") && (
            <button data-testid="notes-save-btn" onClick={() => patch({ admin_notes: notes }, "Notes saved.")} disabled={saving} className="mt-3 flex items-center gap-1.5 rounded-full bg-[var(--pv-raised)] border border-[var(--pv-border)] px-4 py-2 text-xs font-bold text-[var(--pv-text)] hover:bg-[var(--pv-subtle)] disabled:opacity-60">
              <StickyNote className="h-3.5 w-3.5" /> Save notes
            </button>
          )}
        </Section>

        <Section title="Internal tags" testid="profile-tags">
          <div className="flex flex-wrap gap-1.5">
            {TAG_SUGGESTIONS.filter((t) => !tags.includes(t)).map((t) => (
              <button key={t} data-testid={`tag-suggestion-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => addTag(t)} className="rounded-full border border-dashed border-[var(--pv-border-strong)] px-2.5 py-1 text-[11px] font-semibold text-[var(--pv-text-3)] hover:border-cyan-500/30 hover:text-cyan-300">
                + {t}
              </button>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input data-testid="custom-tag-input" value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag(newTag))} placeholder="Custom tag..." className={inputCls} />
            <button data-testid="custom-tag-add-btn" onClick={() => addTag(newTag)} className="shrink-0 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600"><Plus className="h-3.5 w-3.5" /></button>
          </div>
          {financial.payment_history != null && (
          <div className="mt-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Payment history</p>
            {financial.payment_history.length === 0 ? (
              <p className="mt-2 text-xs text-[var(--pv-text-4)]">No payments yet.</p>
            ) : (
              <ul className="mt-2 max-h-48 space-y-1.5 overflow-y-auto">
                {financial.payment_history.map((pay) => (
                  <li key={pay.id} className="flex items-center justify-between gap-2 rounded-xl bg-[var(--pv-subtle)] px-3 py-2 text-xs">
                    <span className="truncate font-medium text-[var(--pv-text-2)]">{pay.job_title || "Job"}{pay.bonus ? <span className="ml-1 text-amber-400">(+{fmtMoney(pay.bonus)} bonus)</span> : null}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-bold text-[var(--pv-text)]">{fmtMoney(pay.amount)}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${pay.status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>{pay.status}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          )}
        </Section>
      </div>
    </div>
  );
};
